import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import AOS from 'aos';
import 'aos/dist/aos.css';
import './Blog.css';

// Import blog images
import blog1 from '../assets/images/blog1.jpg';
import blog2 from '../assets/images/blog2.jpg';
import blog3 from '../assets/images/blog3.jpg';
import blog4 from '../assets/images/blog4.jpg';
import blog5 from '../assets/images/blog5.jpg';
import blog6 from '../assets/images/blog6.jpg';
import blog7 from '../assets/images/blog7.jpg';

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic',
    });
    
    // Simulate loading
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  const categories = [
    { key: 'all', label: 'All Posts', icon: 'bi-grid-3x3-gap' },
    { key: 'visits', label: 'Distinguished Visits', icon: 'bi-people' },
    { key: 'media', label: 'Media Coverage', icon: 'bi-camera-video' },
    { key: 'achievements', label: 'Achievements', icon: 'bi-trophy' }
  ];

  const posts = [
    { 
      id: 1,
      date: '15-04-2024', 
      content: 'Padma Shri awardee Chintakindi Mallesham visited our University. He appreciated our identification of societal problems and innovative approach to solving real-world challenges.',
      icon: 'bi-award',
      image: blog1,
      category: 'visits',
      readTime: '2 min read',
      author: 'University Team',
      tags: ['Padma Shri', 'Innovation', 'Societal Impact']
    },
    { 
      id: 2,
      date: '28-04-2024', 
      content: 'Dr. B. Koteswara Rao - General Surgeon, Dr. P V Krishna Rao, and distinguished medical professionals visited our facility. They appreciated our work and provided valuable feedback for healthcare innovations.',
      icon: 'bi-heart-pulse',
      image: blog2,
      category: 'visits',
      readTime: '3 min read',
      author: 'Medical Team',
      tags: ['Healthcare', 'Medical Innovation', 'Professional Visit']
    },
    { 
      id: 3,
      date: '26-04-2024', 
      content: 'KCP Cements Quality HoDs, Bhava Narayana, V Srinivasa Rao, E&I HoDs Vara Prasad, and K Rama Koteswara Rao visited our research facilities. They appreciated our industrial innovations and provided valuable industry insights.',
      icon: 'bi-building',
      image: blog3,
      category: 'visits',
      readTime: '3 min read',
      author: 'Industrial Relations',
      tags: ['Industry Partnership', 'Quality Management', 'Innovation']
    },
    { 
      id: 4,
      date: '03-05-2024', 
      content: 'Dr. P V Radha Devi, Outstanding Scientist and Director, ADRIN, Dept. of Space, Hyderabad, along with her distinguished team visited our space technology research division. They were impressed by our space-related innovations.',
      icon: 'bi-rocket',
      image: blog4,
      category: 'visits',
      readTime: '2 min read',
      author: 'Space Research Team',
      tags: ['Space Technology', 'ISRO', 'Scientific Research']
    },
    { 
      id: 5,
      date: '16-05-2024', 
      content: 'Sri Yendamuri Veerendranath, renowned Writer and Director from Hyderabad, visited our creative innovation lab. He appreciated our interdisciplinary approach to problem-solving and creative thinking methodologies.',
      icon: 'bi-pen',
      image: blog5,
      category: 'visits',
      readTime: '2 min read',
      author: 'Creative Team',
      tags: ['Creative Writing', 'Innovation', 'Literature']
    },
    { 
      id: 6,
      date: '16-09-2024', 
      content: 'Sri Kondapalli Srinivas, Hon\'ble Minister of MSME, SERP, NRI Empowerment and Relations, Govt. of A.P., visited our entrepreneurship center. He lauded our efforts in fostering innovation and startup ecosystem.',
      icon: 'bi-briefcase',
      image: blog6,
      category: 'achievements',
      readTime: '2 min read',
      author: 'Government Relations',
      tags: ['Government Visit', 'MSME', 'Entrepreneurship']
    },
    { 
      id: 7,
      date: '2024', 
      content: (
        <div className="enterpriseBlog__mediaContent">
          <p className="enterpriseBlog__mediaDescription">
            Our university was featured on ETV Yuva, showcasing our innovative projects and student achievements to a broader audience.
          </p>
          <a 
            href="https://youtu.be/e9LKjmj4guk?feature=shared" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="enterpriseBlog__mediaButton"
          >
            <i className="bi bi-play-circle"></i>
            <span>Watch on ETV Yuva</span>
          </a>
        </div>
      ),
      icon: 'bi-tv',
      image: blog7,
      category: 'media',
      readTime: '5 min watch',
      author: 'Media Relations',
      tags: ['Television', 'Media Coverage', 'Public Recognition']
    },
  ];

  const filteredPosts = posts.filter(post => {
    const contentText = typeof post.content === 'string' 
      ? post.content.toLowerCase()
      : post.tags.join(' ').toLowerCase();
    const matchesSearch = contentText.includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (isLoading) {
    return (
      <div className="enterpriseBlog__loadingContainer">
        <div className="enterpriseBlog__loader">
          <div className="enterpriseBlog__loaderSpinner"></div>
          <p>Loading latest updates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="enterpriseBlog">
      <div className="enterpriseBlog__backgroundWrapper">
        <div className="enterpriseBlog__gradientOverlay"></div>
        <div className="enterpriseBlog__patternOverlay"></div>
      </div>

      {/* Hero Section */}
      <section className="enterpriseBlog__heroSection">
        <div className="enterpriseBlog__heroContainer">
          <motion.div 
            className="enterpriseBlog__heroContent"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="enterpriseBlog__heroBadge" data-aos="fade-down">
              <i className="bi bi-newspaper"></i>
              <span>Latest Updates</span>
            </div>
            <h1 className="enterpriseBlog__heroTitle" data-aos="fade-up" data-aos-delay="100">
              News & <span className="enterpriseBlog__heroTitleAccent">Insights</span>
            </h1>
            <p className="enterpriseBlog__heroSubtitle" data-aos="fade-up" data-aos-delay="200">
              Discover our latest achievements, distinguished visits, and media coverage showcasing our commitment to innovation and excellence
            </p>
            <div className="enterpriseBlog__heroStats" data-aos="fade-up" data-aos-delay="300">
              <div className="enterpriseBlog__statItem">
                <span className="enterpriseBlog__statNumber">{posts.length}</span>
                <span className="enterpriseBlog__statLabel">Updates</span>
              </div>
              <div className="enterpriseBlog__statItem">
                <span className="enterpriseBlog__statNumber">{categories.length - 1}</span>
                <span className="enterpriseBlog__statLabel">Categories</span>
              </div>
              <div className="enterpriseBlog__statItem">
                <span className="enterpriseBlog__statNumber">2025</span>
                <span className="enterpriseBlog__statLabel">Active Year</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="enterpriseBlog__filterSection">
        <div className="enterpriseBlog__filterContainer">
          <div className="enterpriseBlog__filterContent">
            {/* Search Bar */}
            <div className="enterpriseBlog__searchWrapper" data-aos="fade-right">
              <div className="enterpriseBlog__searchContainer">
                <i className="bi bi-search enterpriseBlog__searchIcon"></i>
                <input
                  type="text"
                  className="enterpriseBlog__searchInput"
                  placeholder="Search articles, achievements, or topics..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button 
                    className="enterpriseBlog__searchClear"
                    onClick={() => setSearchTerm('')}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Category Filters */}
            <div className="enterpriseBlog__categoryWrapper" data-aos="fade-left">
              <div className="enterpriseBlog__categoryGrid">
                {categories.map((category, index) => (
                  <motion.button
                    key={category.key}
                    className={`enterpriseBlog__categoryButton ${
                      selectedCategory === category.key ? 'enterpriseBlog__categoryButton--active' : ''
                    }`}
                    onClick={() => setSelectedCategory(category.key)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <i className={`${category.icon} enterpriseBlog__categoryIcon`}></i>
                    <span className="enterpriseBlog__categoryLabel">{category.label}</span>
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Info */}
      <section className="enterpriseBlog__resultsSection">
        <div className="enterpriseBlog__resultsContainer">
          <div className="enterpriseBlog__resultsInfo" data-aos="fade-up">
            <span className="enterpriseBlog__resultsText">
              {filteredPosts.length} {filteredPosts.length === 1 ? 'article' : 'articles'} found
              {selectedCategory !== 'all' && (
                <span className="enterpriseBlog__resultsCategory">
                  in {categories.find(cat => cat.key === selectedCategory)?.label}
                </span>
              )}
            </span>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="enterpriseBlog__postsSection">
        <div className="enterpriseBlog__postsContainer">
          {filteredPosts.length > 0 ? (
            <div className="enterpriseBlog__postsGrid">
              {filteredPosts.map((post, index) => (
                <motion.article 
                  key={post.id} 
                  className="enterpriseBlog__postCard"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                >
                  <div className="enterpriseBlog__postImageContainer">
                    <img
                      src={post.image}
                      alt={typeof post.content === 'string' ? post.content.substring(0, 100) : 'ETV Yuva Feature'}
                      className="enterpriseBlog__postImage"
                      onError={(e) => (e.target.src = 'https://via.placeholder.com/400x250/f8f9fa/6c757d?text=Blog+Image')}
                    />
                    <div className="enterpriseBlog__postBadge">
                      <i className={`${post.icon} enterpriseBlog__postBadgeIcon`}></i>
                    </div>
                    <div className="enterpriseBlog__postOverlay">
                      <div className="enterpriseBlog__postReadTime">
                        <i className="bi bi-clock"></i>
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                  </div>

                  <div className="enterpriseBlog__postContent">
                    <div className="enterpriseBlog__postHeader">
                      <div className="enterpriseBlog__postMeta">
                        <span className="enterpriseBlog__postDate">
                          <i className="bi bi-calendar3"></i>
                          {post.date}
                        </span>
                        <span className="enterpriseBlog__postAuthor">
                          <i className="bi bi-person"></i>
                          {post.author}
                        </span>
                      </div>
                      <div className="enterpriseBlog__postCategory">
                        {categories.find(cat => cat.key === post.category)?.label}
                      </div>
                    </div>

                    <div className="enterpriseBlog__postBody">
                      {typeof post.content === 'string' ? (
                        <p className="enterpriseBlog__postText">{post.content}</p>
                      ) : (
                        post.content
                      )}
                    </div>

                    <div className="enterpriseBlog__postFooter">
                      <div className="enterpriseBlog__postTags">
                        {post.tags.map((tag, tagIndex) => (
                          <span key={tagIndex} className="enterpriseBlog__postTag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          ) : (
            <motion.div 
              className="enterpriseBlog__emptyState"
              data-aos="fade-up"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="enterpriseBlog__emptyIcon">
                <i className="bi bi-search"></i>
              </div>
              <h3 className="enterpriseBlog__emptyTitle">No articles found</h3>
              <p className="enterpriseBlog__emptyText">
                Try adjusting your search criteria or browse all categories
              </p>
              <button 
                className="enterpriseBlog__emptyButton"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                }}
              >
                <i className="bi bi-arrow-clockwise"></i>
                Reset Filters
              </button>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;